import React, { useCallback, useMemo, useState } from "react";
import BadgeSelect from "@/pages/admin-pages/UserManagementPage/components/BadgeSelect.tsx";
import { INCENTIVE_STATUS, INCENTIVE_STATUS_ITEMS } from "@/constants";
import { getFileStorageBadgeVariant } from "@/utils/getIncentiveBadgeVariant.ts";
import { ProjectRecord } from "@/types";
import { useAuthStore } from "@/store/authStore.ts";
import { updateProject } from "@/api/projectsApi";
import { updateContractorProjectReq } from "@/api/contractorApi";
import Snackbar from "@mui/material/Snackbar";
import { Alert, Modal as MuiModal } from "@mui/material";
import NextStepModal from "@/pages/ContractorDashboardPage/components/NextStepModal.tsx";
import DueDateModal from "@/pages/ProjectsPage/components/DueDateModal.tsx";
import { calculateTotalUsableCapacityMWh } from "@/utils/calculateUsableBatteryCapacity.ts";

type Props = {
  tooltipContent?: string | null;
  incentiveStatus: ProjectRecord["incentiveStatus"];
  projectId: ProjectRecord["id"];
  getProjectsReq: () => void;
  usableCapacity?: string;
  numberOfBatteries?: string;
  truncateBadge?: boolean;
};

const IncentiveStatusSelect = ({
  tooltipContent = "",
  incentiveStatus,
  projectId,
  getProjectsReq,
  usableCapacity,
  numberOfBatteries,
  truncateBadge = false,
}: Props) => {
  const [dueDateProjectId, setDueDateProjectId] = useState(0);

  const [changedIncentiveStatus, setChangedIncentiveStatus] = useState(
    "" as INCENTIVE_STATUS
  );
  const [pendingIncentiveStatus, setPendingIncentiveStatus] = useState<
    INCENTIVE_STATUS | ""
  >("");
  const [openBudgetManualModal, setOpenBudgetManualModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNotification, setHasNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<{
    message: string;
    severity: "error" | "success";
  }>({
    message: "",
    severity: "error",
  });

  const handleClose = () => {
    setHasNotification(false);
  };

  const handleCloseBudgetAutoModal = () => {
    setChangedIncentiveStatus("" as INCENTIVE_STATUS);
  };

  const handleCloseBudgetManualModal = () => {
    setOpenBudgetManualModal(false);
    setPendingIncentiveStatus("");
  };

  const handleCloseDueDateExtended = () => {
    setDueDateProjectId(0);
    setPendingIncentiveStatus("");
  };

  const handleConfirmManualUpdate = async () => {
    if (pendingIncentiveStatus) {
      setIsLoading(true);
      try {
        await updateIncentiveProjectStatus(projectId, pendingIncentiveStatus);
        handleCloseBudgetManualModal();
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleConfirmDueDateExtended = async () => {
    if (pendingIncentiveStatus) {
      await updateIncentiveProjectStatus(projectId, pendingIncentiveStatus);
    }
  };

  const role = useAuthStore((state) => state.role);
  const isContractor = role === "contractor";

  const updateIncentiveProjectStatus = async (
    projectId: ProjectRecord["id"],
    value: ProjectRecord["incentiveStatus"]
  ) => {
    try {
      const formData = new FormData();
      Object.keys({ incentiveStatus: value }).forEach((key) => {
        formData.append(key, value as string);
      });

      const response = isContractor
        ? await updateContractorProjectReq(projectId, formData)
        : await updateProject(projectId, formData);
      getProjectsReq();
      setHasNotification(true);
      setNotificationMessage({
        message: response?.data?.project?.message || "Status updated!",
        severity: "success",
      });
      return response;
    } catch (e: any) {
      setHasNotification(true);
      setNotificationMessage({
        message: e?.response?.data?.error,
        severity: e?.response?.data?.success ? "success" : "error",
      });
      console.error(e);
      throw e;
    } finally {
      handleCloseBudgetAutoModal();
    }
  };

  const handleChangeStatus = async (
    projectId: ProjectRecord["id"],
    value: ProjectRecord["incentiveStatus"]
  ) => {
    if (
      value === INCENTIVE_STATUS.RESERVED &&
      incentiveStatus !== INCENTIVE_STATUS.RESERVED
    ) {
      if (
        incentiveStatus === INCENTIVE_STATUS.PENDING ||
        incentiveStatus === INCENTIVE_STATUS.FINANCE_REVIEW
      ) {
        setChangedIncentiveStatus(value);
      } else {
        setOpenBudgetManualModal(true);
        setPendingIncentiveStatus(value);
      }
      return;
    }

    switch (incentiveStatus) {
      case INCENTIVE_STATUS.RESERVED: {
        if (value === INCENTIVE_STATUS.PAID) {
          setChangedIncentiveStatus(value);
        } else if (
          value === INCENTIVE_STATUS.PENDING ||
          value === INCENTIVE_STATUS.NO_HELD ||
          value === INCENTIVE_STATUS.CANCELLED
        ) {
          setOpenBudgetManualModal(true);
          setPendingIncentiveStatus(value);
        } else if (value === INCENTIVE_STATUS.EXTENDED) {
          setPendingIncentiveStatus(value);
          setDueDateProjectId(projectId);
        } else {
          await updateIncentiveProjectStatus(projectId, value);
        }
        break;
      }
      case INCENTIVE_STATUS.PENDING:
      case INCENTIVE_STATUS.FINANCE_REVIEW: {
        await updateIncentiveProjectStatus(projectId, value);
        break;
      }
      case INCENTIVE_STATUS.EXTENDED: {
        if (value === INCENTIVE_STATUS.PAID) {
          setChangedIncentiveStatus(value);
        } else {
          await updateIncentiveProjectStatus(projectId, value);
        }
        break;
      }
      case INCENTIVE_STATUS.PAID: {
        await updateIncentiveProjectStatus(projectId, value);
        break;
      }
      case INCENTIVE_STATUS.UNENROLLED: {
        if (value === INCENTIVE_STATUS.PAID) {
          setChangedIncentiveStatus(value);
        } else {
          await updateIncentiveProjectStatus(projectId, value);
        }
        break;
      }
      default:
        await updateIncentiveProjectStatus(projectId, value);
    }
  };

  const handleNextStep = async () => {
    setIsLoading(true);
    try {
      return await updateIncentiveProjectStatus(
        projectId,
        changedIncentiveStatus
      );
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setIsLoading(false);
      handleCloseBudgetAutoModal();
    }
  };

  const getFromToStatusLabel = useCallback(
    (incentiveStatus: INCENTIVE_STATUS | undefined) => {
      switch (incentiveStatus) {
        case INCENTIVE_STATUS.PENDING:
        case INCENTIVE_STATUS.FINANCE_REVIEW:
          return "Remaining";
        case INCENTIVE_STATUS.RESERVED:
        case INCENTIVE_STATUS.EXTENDED:
          return "Reserved";
        case INCENTIVE_STATUS.PAID: {
          return "Paid";
        }
        case INCENTIVE_STATUS.NO_HELD:
          return "No Incentive Held";
        case INCENTIVE_STATUS.CANCELLED:
          return "Cancelled";
        case INCENTIVE_STATUS.UNENROLLED:
          return "Unenrolled";
        default:
          return "";
      }
    },
    [incentiveStatus]
  );

  const statusLabelTo = getFromToStatusLabel(changedIncentiveStatus);
  const statusLabelFrom = getFromToStatusLabel(incentiveStatus);

  const totalUsableCapacityMWh = useMemo(
    () => calculateTotalUsableCapacityMWh(usableCapacity, numberOfBatteries),
    [usableCapacity, numberOfBatteries]
  );

  const isStatusEnabled = (
    currentStatus: INCENTIVE_STATUS,
    validStatuses: INCENTIVE_STATUS[]
  ) => validStatuses.includes(currentStatus);

  const incentiveSelectItems = useMemo(() => {
    if (incentiveStatus) {
      const itemsWithDisabledState = INCENTIVE_STATUS_ITEMS.map((el) => {
        const { value } = el;

        const isDisabled = (() => {
          switch (value) {
            case INCENTIVE_STATUS.PENDING:
              return !isStatusEnabled(incentiveStatus, [
                INCENTIVE_STATUS.RESERVED,
                INCENTIVE_STATUS.PENDING,
                INCENTIVE_STATUS.NO_HELD,
                INCENTIVE_STATUS.FINANCE_REVIEW,
              ]);
            case INCENTIVE_STATUS.PAID: {
              return !isStatusEnabled(incentiveStatus, [
                INCENTIVE_STATUS.RESERVED,
                INCENTIVE_STATUS.EXTENDED,
                INCENTIVE_STATUS.PAID,
                INCENTIVE_STATUS.FINANCE_REVIEW,
              ]);
            }
            case INCENTIVE_STATUS.EXTENDED:
              return !isStatusEnabled(incentiveStatus, [
                INCENTIVE_STATUS.RESERVED,
                INCENTIVE_STATUS.EXTENDED,
                INCENTIVE_STATUS.FINANCE_REVIEW,
              ]);
            case INCENTIVE_STATUS.NO_HELD:
              return !isStatusEnabled(incentiveStatus, [
                INCENTIVE_STATUS.RESERVED,
                INCENTIVE_STATUS.NO_HELD,
                INCENTIVE_STATUS.CANCELLED,
                INCENTIVE_STATUS.FINANCE_REVIEW,
              ]);
            case INCENTIVE_STATUS.RESERVED:
              return !isStatusEnabled(incentiveStatus, [
                INCENTIVE_STATUS.RESERVED,
                INCENTIVE_STATUS.PAID,
                INCENTIVE_STATUS.NO_HELD,
                INCENTIVE_STATUS.EXTENDED,
                INCENTIVE_STATUS.PENDING,
                INCENTIVE_STATUS.FINANCE_REVIEW,
              ]);
            case INCENTIVE_STATUS.CANCELLED:
              return !isStatusEnabled(incentiveStatus, [
                INCENTIVE_STATUS.RESERVED,
                INCENTIVE_STATUS.NO_HELD,
                INCENTIVE_STATUS.EXTENDED,
                INCENTIVE_STATUS.PENDING,
                INCENTIVE_STATUS.UNENROLLED,
                INCENTIVE_STATUS.FINANCE_REVIEW,
              ]);
            case INCENTIVE_STATUS.UNENROLLED:
              return !isStatusEnabled(incentiveStatus, [
                INCENTIVE_STATUS.PAID,
                INCENTIVE_STATUS.FINANCE_REVIEW,
              ]);
            default:
              return false;
          }
        })();

        return { ...el, disabled: isDisabled };
      });
      return itemsWithDisabledState.sort(
        (a, b) => Number(a.disabled) - Number(b.disabled)
      );
    }
  }, [incentiveStatus]);

  return (
    <>
      <BadgeSelect
        tooltipContent={tooltipContent}
        readOnly={role !== "administrator"}
        currentSelectedItem={incentiveStatus as string}
        items={incentiveSelectItems ?? []}
        onSelectItem={(value) =>
          handleChangeStatus(projectId, value as INCENTIVE_STATUS)
        }
        badgeVariantFn={getFileStorageBadgeVariant}
        truncateBadge={truncateBadge}
      />
      {hasNotification && (
        <Snackbar
          open={hasNotification}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert onClose={handleClose} severity={notificationMessage?.severity}>
            {notificationMessage?.message}
          </Alert>
        </Snackbar>
      )}
      <MuiModal
        open={!!changedIncentiveStatus}
        onClose={handleCloseBudgetAutoModal}
      >
        <NextStepModal
          onClick={handleNextStep}
          onClose={handleCloseBudgetAutoModal}
          title={`This status change will lead to a Budget update`}
          subtitle={`${totalUsableCapacityMWh} MWh will be transferred from ${statusLabelFrom} to ${statusLabelTo}`}
          buttonText={"Continue"}
          loading={isLoading}
        />
      </MuiModal>
      <MuiModal
        open={openBudgetManualModal}
        onClose={handleCloseBudgetManualModal}
      >
        <NextStepModal
          onClose={handleCloseBudgetManualModal}
          title={`This action will affect the budget status.`}
          subtitle={`After updating the status, make sure to adjust the budget amount manually`}
          buttonText={"Continue"}
          onClick={handleConfirmManualUpdate}
          loading={isLoading}
        />
      </MuiModal>
      <DueDateModal
        projectId={dueDateProjectId}
        onClose={handleCloseDueDateExtended}
        getProjectsReq={getProjectsReq}
        updateIncentiveStatus={handleConfirmDueDateExtended}
      />
    </>
  );
};

export default IncentiveStatusSelect;
